// components/form/PasswordField.tsx
import React, { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import BaseTextField from '@/components/form/BaseTextField';

type PasswordFieldProps = Omit<React.ComponentProps<typeof BaseTextField>, 'type' | 'endIcon' | 'inputRef'> & {
    ariaLabelShow?: string;
    ariaLabelHide?: string;
};

function safeLen(v: unknown): number {
    if (typeof v === 'string') return v.length;
    try {
        return v == null ? 0 : String(v).length;
    } catch {
        return 0;
    }
}

const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(function PasswordField(
    { ariaLabelShow = 'Show password', ariaLabelHide = 'Hide password', sx, ...props },
    forwardedRef,
) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const setRefs = (el: HTMLInputElement | null) => {
        inputRef.current = el;
        if (typeof forwardedRef === 'function') forwardedRef(el);
        else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
    };

    const [visible, setVisible] = useState(false);
    const sRef = useRef<number | null>(null);
    const eRef = useRef<number | null>(null);

    const toggle = useCallback(() => {
        const el = inputRef.current;
        if (el) {
            el.focus();
            sRef.current = el.selectionStart ?? null;
            eRef.current = el.selectionEnd ?? null;
        }
        setVisible((v) => !v);
    }, []);

    useLayoutEffect(() => {
        const el = inputRef.current;
        if (!el) return;

        // flip DOM attribute before paint, React prop stays "password"
        try {
            el.type = visible ? 'text' : 'password';
        } catch {}

        el.focus();
        const len = safeLen(el.value);
        const start = sRef.current ?? len;
        const end = eRef.current ?? start;
        try {
            el.setSelectionRange(start, end);
        } catch {}

        sRef.current = eRef.current = null;
    }, [visible]);

    const eye = (
        <IconButton
            aria-label={visible ? ariaLabelHide : ariaLabelShow}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
                e.preventDefault();
                toggle();
            }}
            edge="end"
        >
            {visible ? <VisibilityOff /> : <Visibility />}
        </IconButton>
    );

    return (
        <BaseTextField
            {...props}
            type="password" // keep constant; we flip DOM type ourselves
            inputRef={setRefs}
            endIcon={<InputAdornment position="end">{eye}</InputAdornment>}
            sx={{
                '& input::-ms-reveal, & input::-ms-clear': { display: 'none' }, // hide native Edge eye
                ...sx,
            }}
        />
    );
});

export default PasswordField;
